import { queryOptions, UseMutationOptions } from "@tanstack/react-query";
import instance from "./axiosInstance";
import { Uppdrag, UppdragStatus } from "types/uppdrag.type";
import { InspectionType } from "types/enums";
import { TUppdragSchema } from "pages/app/uppdrag/overview/uppdragSchema";
import { z } from "zod";
import { FileDTO } from "./fiveYearsInspection.api";
import { TCreateInspectionSchema } from "pages/app/uppdrag/uppgifter/useCreateUppgiftLogic";
import { ActivityType } from "enums/ActivityType.enum";

const ENDPOINT = "/uppdrag";

export enum UppdragQueryKey {
  GetList = "get-uppdrag-list",
  GetOne = "get-uppdrag",
}

export const getUppdrag = (uuid: string) =>
  queryOptions({
    queryKey: [UppdragQueryKey.GetOne, uuid],
    queryFn: async (): Promise<Uppdrag> =>
      (await instance.get(`${ENDPOINT}/${uuid}`)).data,
  });

export type GetUppdragSortBy =
  | "customerName"
  | "scheduleDate"
  | "status"
  | "assignedToUser"
  | "id";

interface GetUppdragListParams {
  // companyId: number;
  limit?: number;
  offset?: number;
  searchQuery?: string;
  sortBy?: GetUppdragSortBy;
  direction?: "DESC" | "ASC";
  isKlarmarkerad?: boolean;
  isWaitingForNvReport?: boolean;
  isWaitingForBilagor?: boolean;
  isWaitingForInvoicing?: boolean;
  statuses?: UppdragStatus[];
  assignedToUserId?: number;
}

export type GetUppdragListResponse = {
  items: Uppdrag[];
  total: number;
};

export const getUppdragList = (params: GetUppdragListParams) =>
  queryOptions({
    queryKey: [UppdragQueryKey.GetList, params],
    queryFn: async (): Promise<GetUppdragListResponse> =>
      (await instance.get(ENDPOINT, { params })).data,
  });

export type FacilityUuid = {
  facilityUuid: string;
};

export type ParentCustomerUuid = {
  parentCustomerUuid?: string;
};

export type SeparatorUppgiftList = {
  separatorUppgiftList?: SeparatorUppgiftDto[];
};

export type SeparatorUppgiftDto = {
  separatorId: number;
  activityType: ActivityType;
};

type CreateUppdragV2Data = {
  customerUuid: string;
  facilityUuid: string;
  parentCustomerUuid?: string;
  assignedToUserUuid: string;
  scheduleDate: Date;
  scheduleDateType: string;
  isPrioritized: boolean;
  description?: string;
  referenceNumber?: string;
  notes?: string;
  estimatedWorkingHours?: string;
  estimatedDrivingHours?: string;
  separatorUuid?: string;
  uppgifter: {
    tomning: boolean;
    sixMonths: boolean;
    fiveYears: boolean;
    waterMeasurement?: boolean;
  };
  wasteCodeUuid?: string;
  wasteStationUuid?: string;
  preliminaryWeight?: string;
  tomningType?: string;
  status?: UppdragStatus;
  separatorUppgiftList?: SeparatorUppgiftDto[];
  wasteItemList?: {
    separatorUuid: string;
    wasteCodeUuid: string;
    preliminaryWeight?: string;
  }[];
  wasteStationDeliveryDate?: Date | null;
};

export const createUppdragV2 = () => ({
  mutationFn: async (data: CreateUppdragV2Data): Promise<Uppdrag> =>
    (await instance.post(ENDPOINT, data)).data,
});

export type PatchUppdragDto = {};

type PatchUppdragData = {
  payload: Partial<TUppdragSchema>;
  id: number;
};
export const patchUppdrag = () => ({
  mutationFn: async ({ id, payload }: PatchUppdragData): Promise<Uppdrag> =>
    (await instance.patch(`${ENDPOINT}/${id}`, payload)).data,
});

export const deleteUppdrag = (id: number): UseMutationOptions => ({
  mutationFn: async (): Promise<void> =>
    (await instance.delete(`${ENDPOINT}/${id}`)).data,
});

type CreateUppdragCommentDto = {
  comment: string;
  uppdragId: number;
};

export const createUppdragComment = () => ({
  mutationFn: async ({
    comment,
    uppdragId,
  }: CreateUppdragCommentDto): Promise<Uppdrag> =>
    (await instance.post(`${ENDPOINT}/${uppdragId}/comment`, { comment })).data,
});

export const deleteUppdragComment = (
  uppdragId: number,
  commentId: number,
): UseMutationOptions => ({
  mutationFn: async (): Promise<void> =>
    (await instance.delete(`${ENDPOINT}/${uppdragId}/comment/${commentId}`))
      .data,
});

type CreateUppdragInspectionData = {
  uppdragId: number;
  payload: TCreateInspectionSchema;
};

export const createUppdragInspection = () => ({
  mutationFn: async (data: CreateUppdragInspectionData): Promise<Uppdrag> =>
    (
      await instance.post(
        `${ENDPOINT}/${data.uppdragId}/inspection`,
        data.payload,
      )
    ).data,
});

type DeleteUppdragInspectionDto = {
  inspectionUuid: string;
  inspectionType: InspectionType;
};

type DeleteInspectionInput = {
  uppdragId: number;
  payload: DeleteUppdragInspectionDto;
};

export const deleteUppdragInspection = () => ({
  mutationFn: async ({
    uppdragId,
    payload,
  }: DeleteInspectionInput): Promise<void> =>
    (
      await instance.delete(`${ENDPOINT}/${uppdragId}/inspection`, {
        data: payload,
      })
    ).data,
});

export const transportSedelSchema = z.object({
  wasteStationUuid: z
    .string({
      required_error: "Obligatoriskt fält",
      message: "Obligatoriskt fält",
    })
    .uuid(),
  timeOfDepartureUtc: z.coerce.date().nullable().optional(),
  vehicleUuid: z.string().uuid().optional(),
  customerSignatureUnavailable: z.boolean().nullable().optional(),
  wasteStationSignatureUnavailable: z.boolean().nullable().optional(),
  wasteStationDeliveryDate: z.coerce.date().nullable().optional(),
});

export type TTransportSedelSchema = z.infer<typeof transportSedelSchema>;

export const createTransportSedel = (id: number) => ({
  mutationFn: async (dto: TTransportSedelSchema): Promise<Uppdrag> =>
    (await instance.post(`${ENDPOINT}/${id}/transport-sedel`, dto)).data,
});

export type CreateTransportSedelWithNotificationDto = {
  wasteStationUuid: string;
  vehicleUuid?: string;
  timeOfDepartureUtc?: Date | null;
  wasteStationDeliveryDate: Date;
  wasteCodeUuid: string;
  weightPreliminary: string;
  tomningSeparatorUuid?: string | null;
  tomningType?: "Heltomning" | "Toppsugning" | "NotApplicable" | "NotAssigned";
  comment?: string;
};

export const createTransportSedelWithNotification = (id: number) => ({
  mutationFn: async (
    dto: CreateTransportSedelWithNotificationDto,
  ): Promise<Uppdrag> =>
    (
      await instance.post(
        `${ENDPOINT}/${id}/transport-sedel-with-notification`,
        dto,
      )
    ).data,
});

type CreateUppdragFilesData = {
  uppdragId: number;
  payload: { files: FileDTO[] };
};

export const createUppdragFiles = () => ({
  mutationFn: async (data: CreateUppdragFilesData) =>
    await instance.put(`${ENDPOINT}/${data.uppdragId}/files`, data.payload),
});

type DeleteFilePayload = {
  uppdragId: number;
  fileId: number;
};

export const deleteUppdragFile = () => ({
  mutationFn: async (data: DeleteFilePayload) =>
    await instance.delete(`${ENDPOINT}/${data.uppdragId}/files/${data.fileId}`),
});

// Cleanup after waste station sign types
export type CleanupAfterWasteStationSignItemDto = {
  uppdragId: number;
  reportedWorkingHours?: string;
  reportedDrivingHours?: string;
  isCompleted?: boolean;
};

export type CleanupAfterWasteStationSignDto = {
  items: CleanupAfterWasteStationSignItemDto[];
};

export const cleanupAfterWasteStationSign = () => ({
  mutationFn: async (dto: CleanupAfterWasteStationSignDto): Promise<any> =>
    (await instance.post(`${ENDPOINT}/cleanup-after-waste-station-sign`, dto))
      .data,
});

type SendUppdragAttachmentsInput = {
  uppdragId: number;
  payload: {
    toCustomer?: boolean;
    toParentCustomer?: boolean;
    toWasteStation?: boolean;
  };
};
export const sendUppdragAttachments = () => ({
  mutationFn: async (data: SendUppdragAttachmentsInput) =>
    await instance.post(
      `${ENDPOINT}/${data.uppdragId}/send-attachments`,
      data.payload,
    ),
});
